import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  clearAllUserSession,
  clearExpiredPcAuthSession,
  getStoredAccessToken,
  getStoredPcAuthUser,
  getValidDirectAccessToken,
  isStrictSessionExpired,
  loadDirectSession,
  logoutDirectSession,
} from './pcAuthBrowserFlow';
import { isAllowedOnCurrentHost } from './testPortalAccess';

const noopAsync = async () => undefined;
const noop = () => undefined;

const PcAuthContext = React.createContext({
  isAuthenticated: false,
  isLoading: true,
  loading: true,
  user: undefined,
  error: null,
  loginWithRedirect: noopAsync,
  getAccessTokenSilently: noopAsync,
  getIdTokenClaims: noopAsync,
  getTokenWithPopup: noopAsync,
  getAccessTokenWithPopup: noopAsync,
  getUserInfo: noopAsync,
  logout: noop,
});

const getConfiguredEndpoint = (value, fallback) => {
  const configuredValue = (value || '').trim();
  return configuredValue || fallback;
};

const toAbsoluteUrl = (value) => {
  if (!value) {
    return value;
  }

  try {
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }
    return new URL(value, window.location.origin).toString();
  } catch (error) {
    return value;
  }
};

const decodeJwtPayload = (token) => {
  if (!token || token.split('.').length < 2) {
    return undefined;
  }

  try {
    const payloadSegment = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload =
      payloadSegment + '='.repeat((4 - (payloadSegment.length % 4 || 4)) % 4);
    return JSON.parse(atob(paddedPayload));
  } catch (error) {
    return undefined;
  }
};

export const usePcAuth = () => useContext(PcAuthContext);

const PcAuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    isLoading: true,
    loading: true,
    user: undefined,
    error: null,
  });
  const logoutEndpoint = useMemo(
    () => getConfiguredEndpoint(process.env.REACT_APP_PC_AUTH_LOGOUT_URL, '/auth/logout'),
    []
  );
  const startEndpoint = useMemo(
    () => getConfiguredEndpoint(process.env.REACT_APP_PC_AUTH_START_URL, '/auth/start'),
    []
  );

  const loadSessionFromDirectFlow = useCallback(async () => {
    if (isStrictSessionExpired()) {
      clearExpiredPcAuthSession();
      return false;
    }

    const storedAccessToken = getStoredAccessToken();
    const storedUser = getStoredPcAuthUser();
    if (storedAccessToken && storedUser) {
      const tokenClaims = decodeJwtPayload(storedAccessToken);
      if (!isAllowedOnCurrentHost(storedUser) && !isAllowedOnCurrentHost(tokenClaims)) {
        clearAllUserSession();
        return false;
      }
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        loading: false,
        user: storedUser,
        error: null,
      });
      return true;
    }
    const directSession = await loadDirectSession();
    if (!directSession?.user) {
      return false;
    }

    const sessionClaims = decodeJwtPayload(directSession.accessToken);
    if (!isAllowedOnCurrentHost(directSession.user) && !isAllowedOnCurrentHost(sessionClaims)) {
      clearAllUserSession();
      return false;
    }

    setAuthState({
      isAuthenticated: true,
      isLoading: false,
      loading: false,
      user: directSession.user,
      error: null,
    });
    return true;
  }, []);

  const loadSession = useCallback(async () => {
    if (await loadSessionFromDirectFlow()) {
      return;
    }
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      loading: false,
      user: undefined,
      error: null,
    });
  }, [loadSessionFromDirectFlow]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const loginWithRedirect = useCallback(
    async (options = {}) => {
      const authorizationParams = options?.authorizationParams || {};
      const returnTo =
        options?.appState?.targetUrl ||
        options?.redirect_uri ||
        authorizationParams?.redirect_uri ||
        `${window.location.pathname}${window.location.search}`;
      const loginHint = options?.login_hint || authorizationParams?.login_hint;

      try {
        const resolvedStartUrl = startEndpoint.startsWith('http://') || startEndpoint.startsWith('https://')
          ? new URL(startEndpoint)
          : new URL(startEndpoint, window.location.origin);

        if (returnTo) {
          resolvedStartUrl.searchParams.set('returnTo', returnTo);
        }

        if (loginHint) {
          resolvedStartUrl.searchParams.set('email', loginHint);
        }

        window.location.assign(resolvedStartUrl.toString());
      } catch (error) {
        window.location.assign(startEndpoint);
      }
    },
    [startEndpoint]
  );

  const getAccessTokenSilently = useCallback(async () => {
    if (isStrictSessionExpired()) {
      clearExpiredPcAuthSession();
      setAuthState((previousState) => ({
        ...previousState,
        isAuthenticated: false,
        isLoading: false,
        loading: false,
        user: undefined,
        error: null,
      }));
      throw new Error('Session expired after 24 hours. Please sign in again.');
    }

    // Within the 24-hour session window, return a usable access token.
    // If the JWT is near/past expiry, refresh via the stored refresh token
    // instead of forcing the user back through login.
    const accessToken = await getValidDirectAccessToken();
    if (accessToken) {
      return accessToken;
    }

    setAuthState((previousState) => ({
      ...previousState,
      isAuthenticated: false,
      isLoading: false,
      loading: false,
      user: undefined,
      error: null,
    }));
    throw new Error('No active session token found.');
  }, []);

  const getIdTokenClaims = useCallback(async () => {
    const token = getStoredAccessToken();
    return decodeJwtPayload(token);
  }, []);

  const logout = useCallback(
    async (options = {}) => {
      const logoutParams = options?.logoutParams || {};
      const returnTo = options?.returnTo || logoutParams?.returnTo || '/';

      try {
        await fetch(toAbsoluteUrl(logoutEndpoint), {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        });
      } catch (error) {
        // Best effort logout.
      }

      await logoutDirectSession();

      setAuthState((previousState) => ({
        ...previousState,
        isAuthenticated: false,
        user: undefined,
      }));

      if (returnTo) {
        window.location.assign(returnTo);
      }
    },
    [logoutEndpoint]
  );

  const getUserInfo = useCallback(async () => authState.user, [authState.user]);

  const contextValue = useMemo(
    () => ({
      ...authState,
      loading: authState.isLoading,
      loginWithRedirect,
      getAccessTokenSilently,
      getIdTokenClaims,
      getTokenWithPopup: getAccessTokenSilently,
      getAccessTokenWithPopup: getAccessTokenSilently,
      getUserInfo,
      logout,
      refreshSession: loadSession,
    }),
    [authState, getAccessTokenSilently, getIdTokenClaims, getUserInfo, loadSession, loginWithRedirect, logout]
  );

  return <PcAuthContext.Provider value={contextValue}>{children}</PcAuthContext.Provider>;
};

export default PcAuthProvider;
